import { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { prop } from 'ramda';

import { useAppSelector } from '@core/js/store/hooks';
import { post } from '@core/js/utils/fetch.util';

import { useDataFetchWithAwait } from '@interlayer/js/app/hooks/useDataFetchWithAwait';
import { useDynamicTags } from '@interlayer/js/app/hooks/useDynamicTags';
import { usePostCodeSelector } from '@interlayer/js/app/store/hooks/usePostCodeSelector';
import { setClubData, setGeoSearch } from '@interlayer/js/app/store/reducers/postCodeFinder/actions';
import { TClubGeocodeResponse } from '@interlayer/js/app/store/reducers/postCodeFinder/types';
import { DYNAMIC_TAG_NAMES } from '@interlayer/js/app/utils/constants';

export const useGetGeocode = () => {
    const dispatch = useDispatch();
    const defaultCountry = useAppSelector(prop('defaultCountry'));
    const { geoSearch } = usePostCodeSelector();
    const [
        dTagPostcodeInternationalGeocodingKey,
        dTagPostcodeInternationalGeocodingUrl
    ] = useDynamicTags([
        DYNAMIC_TAG_NAMES.postcodeInternationalGeocodingKey,
        DYNAMIC_TAG_NAMES.postcodeInternationalGeocodingUrl
    ]);

    const getForGeocode = useCallback(async (query: string): Promise<string[]> => {
        const response: TClubGeocodeResponse = await post({
            url: dTagPostcodeInternationalGeocodingUrl,
            requestBody: {
                Key: dTagPostcodeInternationalGeocodingKey,
                Country: defaultCountry,
                Input: [query]
            }
        });
        const isError = Boolean(response.Number) || Boolean(response.Items[0].Error);
        const queryGeo = isError
            ? []
            : [response.Items[0]?.Results[0].Latitude, response.Items[0]?.Results[0].Longitude].filter(Boolean);

        if (!(query in geoSearch) && !isError) {
            dispatch(setGeoSearch({ [query]: queryGeo }));
        }

        dispatch(setClubData({
            name: query
        }));

        return queryGeo;
    }, [dTagPostcodeInternationalGeocodingUrl, dTagPostcodeInternationalGeocodingKey, defaultCountry, geoSearch, dispatch]);

    const checkIsData = useCallback(({ paramsFetch: query }: { paramsFetch?: string }): false | string[] => {
        if (query && query in geoSearch) {
            dispatch(setClubData({
                name: query
            }));

            return geoSearch[query];
        }

        return false;
    }, [dispatch, geoSearch]);

    return useDataFetchWithAwait({
        fetch: getForGeocode,
        checkIsData
    });
};
